import {
  AccountList,
  CurrentUser,
  GetTokenReqType,
  UserInfo,
  StorageLocationPayload,
  Roles,
} from 'interfaces'
import {AxiosError, AxiosResponse} from 'axios'
import {unwrapAPIError} from 'utils'
import {getAuthBaseUrl, requestHandler} from '../HTTP'
import {EndPoints, HttpMethods} from '../HTTP/HTTP.types'
import {store} from 'app/store'
import {queryClient} from 'services/queryClient'
import {secs35} from 'definitions/constant'

const login = async (data: {
  email: string
  password: string
  keepSignedIn: boolean
}): Promise<AxiosResponse<{message: CurrentUser}>> => {
  try {
    return await requestHandler<{message: CurrentUser}, typeof data>({
      method: HttpMethods.POST,
      url: EndPoints.Login,
      data,
    })
  } catch (error: unknown) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const logout = async (): Promise<AxiosResponse<{message: string}>> => {
  try {
    const res = await requestHandler<{message: string}>({
      method: HttpMethods.POST,
      url: EndPoints.Logout,
    })
    if (res) {
      store.dispatch({type: 'reset/store'})
      queryClient.clear()
    }
    return res
  } catch (error: unknown) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getTokens = async (
  data: GetTokenReqType,
): Promise<AxiosResponse<UserInfo>> => {
  try {
    const params = {
      token: data.token,
    }

    if (data.tenants) {
      params['tenant'] = data.tenants
    }

    return await requestHandler<UserInfo>({
      method: HttpMethods.GET,
      url: EndPoints.GetTokens,
      options: {
        params,
      },
    })
  } catch (error: unknown) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const downloadInstaller = async (data: {
  bundle: string
}): Promise<AxiosResponse<{fileUrl: string}>> => {
  try {
    return await requestHandler<{fileUrl: string}, typeof data>({
      method: HttpMethods.POST,
      url: EndPoints.GenerateInstaller,
      data,
      options: {
        timeout: secs35,
      },
    })
  } catch (error: unknown) {
    const errorValue = unwrapAPIError(error)
    const axiosError = error as AxiosError
    const status =
      axiosError.response?.status ??
      (typeof axiosError === 'object' && axiosError !== null
        ? (axiosError as {status?: number}).status
        : undefined)
    return Promise.reject({status, message: errorValue})
  }
}

const getAccountList = async (): Promise<AxiosResponse<AccountList>> => {
  try {
    return await requestHandler<AccountList>({
      method: HttpMethods.GET,
      url: EndPoints.SwitchAccount,
    })
  } catch (error: unknown) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const storeLocation = async (
  data: StorageLocationPayload,
): Promise<AxiosResponse<{message: string}>> => {
  try {
    return await requestHandler<{message: string}, typeof data>({
      method: HttpMethods.POST,
      url: EndPoints.DataResidency,
      data,
      options: {
        timeout: secs35,
      },
    })
  } catch (error: unknown) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const authBaseUrl = getAuthBaseUrl()

const getRoles = async (): Promise<AxiosResponse<Roles[]>> => {
  try {
    return await requestHandler<Roles[]>({
      method: HttpMethods.GET,
      // Override axios baseUrl
      url: authBaseUrl + EndPoints.GetRoles,
    })
  } catch (error: unknown) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

export const AuthService = {
  login,
  getTokens,
  logout,
  downloadInstaller,
  getAccountList,
  storeLocation,
  getRoles,
}
